/**
 * Constants and Data for SprintRay Order Form
 *
 * NOTE: Translations have moved to JSON files in the translations/ folder.
 * Import { getTranslations } from './translations' instead of using SPANISH_TEXT/GERMAN_TEXT.
 * The constants below are kept for backward compatibility and PHP migration fallback.
 */

import {
	Product,
	Promotion,
	FreeResinOption,
	UIText,
	VariantOption,
} from './types';

// Spanish UI Text
export const SPANISH_TEXT: UIText = {
	header: {
		title: 'EXPODENTAL',
		website: 'www.sprintray.com/es-es',
		email: 'orders.eu@sprintray.com',
	},
	customerInfo: {
		title: 'Información del cliente',
		nombreClinica: 'Nombre clinica/laboratorio',
		nombre: 'Nombre',
		apellidos: 'Apellidos',
		direccion: 'Dirección',
		codigoPostal: 'Código Postal',
		ciudad: 'Ciudad',
		pais: 'Pais',
		email: 'Email',
		telefono: 'Teléfono',
		required: '*',
	},
	tradePartner: {
		title: 'Distribuidor',
		distribuidor: 'Distribuidor',
		nombreFachberater: 'Nombre Asesor',
		selectDistributor: 'Seleccionar distribuidor',
		selectCountryFirst: 'Seleccione primero un país',
		distribuidorManualPlaceholder: 'Introduzca el nombre del distribuidor',
		manualEntryHint: 'No hay distribuidores registrados para este país — introduzca el nombre manualmente.',
	},
	products: {
		title: 'Productos',
		addToCart: 'Añadir',
		quantity: 'Cantidad',
		selectVariant: 'Seleccionar variante...',
		categories: {
			Hardware: 'Hardware',
			Resins: 'Resinas',
			'Cápsulas Midas': 'Cápsulas Midas',
			Accessories: 'Accesorios',
		},
	},
	promotions: {
		title: 'PROMOCIONES',
		validUntil: 'Válido hasta 20.03.2026',
		add: 'Añadir',
		added: 'Añadido',
	},
	freeResins: {
		title: '1.000€ en resinas GRATIS',
		subtitle: 'Seleccione sus resinas gratuitas (máximo 1.000€)',
		budget: 'Presupuesto utilizado',
		select: 'Seleccionar',
		disabledMessage:
			'Añada un sistema Pro 2 o un pack + para activar las resinas gratuitas',
	},
	cart: {
		empty: 'No hay productos en el carrito',
		title: 'Resumen del Pedido',
	},
	footer: {
		remarks: 'Observaciones',
		disclaimer:
			'Todos los precios son en euros y sin IVA.\n\nUna vez comprado el sistema a través de tu distribuidor, envía un email a orders.eu@sprintray.com con el comprobante de pago o factura para que podamos enviarte las resinas gratuitas.',
		signature: 'Fecha y firma del cliente',
		clearSignature: 'Borrar',
		submitOrder: 'Enviar Pedido',
		submitting: 'Enviando...',
		success: '¡Pedido enviado con éxito!',
		error: 'Error al enviar el pedido. Por favor, inténtelo de nuevo.',
		offline: 'Modo Sin Conexión',
		offlineMessage:
			'Su pedido se guardará localmente y se enviará cuando tenga conexión.',
		online: 'En línea',
		syncing: 'Sincronizando',
		pendingOrders: 'pedidos pendientes',
		pendingOrder: 'pedido pendiente',
	},
	print: {
		title: 'Solicitud de Presupuesto',
		eventName: 'Nombre del evento',
	},
};

// Product Data
const RESIN_VARIANTS: VariantOption[] = [
	{ value: 'A1', label: 'A1' },
	{ value: 'A2', label: 'A2' },
	{ value: 'A3', label: 'A3' },
	{ value: 'B1', label: 'B1' },
	{ value: 'C2', label: 'C2' },
	{ value: 'D2', label: 'D2' },
	{ value: 'Bleach', label: 'Bleach' },
	{ value: 'Hollywood White', label: 'Hollywood White' },
	{ value: 'unpigmented', label: 'Unpigmented' },
	{ value: 'Dune', label: 'Dune' },
	{ value: 'Graphite', label: 'Graphite' },
	{ value: '4 colores', label: '4 colores disponibles' },
];

const A1_A2_A3_B1_BLEACH = RESIN_VARIANTS.filter( ( v ) =>
	[ 'A1', 'A2', 'A3', 'B1', 'Bleach' ].includes( v.value )
);

const A1_BLEACH = RESIN_VARIANTS.filter( ( v ) =>
	[ 'A1', 'Bleach' ].includes( v.value )
);

const DUNE_GRAPHITE = RESIN_VARIANTS.filter( ( v ) =>
	[ 'Dune', 'Graphite' ].includes( v.value )
);

// Helper to add translations to a product
const withTranslations = (
	product: Product,
	translations: {
		es?: { name?: string };
		de?: { name?: string };
		it?: { name?: string };
	}
): Product => ( { ...product, translations } );

export const PRODUCTS: Product[] = [
	// Hardware
	withTranslations(
		{
			id: 'hardware-pro2',
			category: 'Hardware',
			name: 'SprintRay Pro 2',
			price: 10490,
			notes: '' /* 'Includes tanque, plataforma, Pro Optical Panel', */,
			serialNumber: 'SRI-0102046',
			imagePlaceholder: 'SprintRay Pro 2',
			imagePath:
				'assets/images/products/Pro_2_3-quarter-closed-right-Punchier.png',
		},
		{
			es: { name: 'SprintRay Pro 2' },
			de: { name: 'SprintRay Pro 2' },
		}
	),
	withTranslations(
		{
			id: 'hardware-midas',
			category: 'Hardware',
			name: 'SprintRay Midas',
			price: 10490,
			serialNumber: 'SRI-0102047',
			imagePlaceholder: 'SprintRay Midas',
			imagePath: 'assets/images/products/Midas-Hero-front.png',
		},
		{
			es: { name: 'SprintRay Midas' },
			de: { name: 'SprintRay Midas' },
		}
	),
	withTranslations(
		{
			id: 'hardware-prowash',
			category: 'Hardware',
			name: 'ProWash S',
			price: 2990,
			serialNumber: 'SRI-0504012',
			imagePlaceholder: 'ProWash S',
			imagePath:
				'assets/images/products/ProWash-S-3QuarterLeft_Lightened.png',
		},
		{
			es: { name: 'ProWash S' },
			de: { name: 'ProWash S' },
		}
	),
	withTranslations(
		{
			id: 'hardware-nanocure',
			category: 'Hardware',
			name: 'NanoCure',
			price: 3490,
			serialNumber: 'SRI-0504025',
			imagePlaceholder: 'NanoCure',
			imagePath:
				'assets/images/products/NanoCure-SprintRay-3quarter-right-PerspectiveShiftforEcosystem.png',
		},
		{
			es: { name: 'NanoCure' },
			de: { name: 'NanoCure' },
		}
	),
	// Resins
	withTranslations(
		{
			id: 'resin-ceramic-crown',
			category: 'Resins',
			name: 'Ceramic Crown 250g',
			price: 390,
			variants: A1_A2_A3_B1_BLEACH,
			serialNumber: 'SRI-0202057',
			imagePlaceholder: 'Ceramic Crown',
			imagePath:
				'assets/images/products/SR_Resin_CeramicCrown_Bleach_0202056.png',
		},
		{
			es: { name: 'Ceramic Crown 250g' },
			de: { name: 'Keramikkrone 250g' },
		}
	),
	withTranslations(
		{
			id: 'resin-onx-tough',
			category: 'Resins',
			name: 'OnX Tough 2 500g',
			price: 749,
			variants: [
				...A1_BLEACH,
				{ value: 'Hollywood White', label: 'Hollywood White' },
			],
			serialNumber: 'SRI-0202164',
			imagePlaceholder: 'OnX Tough 2',
			imagePath:
				'assets/images/products/SRI-0202164-SprintRay-OnX-Tough-2-Hollywood-White-500g.png',
		},
		{
			es: { name: 'OnX Tough 2 500g' },
			de: { name: 'OnX Tough 2 500g' },
		}
	),
	withTranslations(
		{
			id: 'resin-die-model',
			category: 'Resins',
			name: 'Die & Model 2 Tan/Gray 1kg',
			price: 149,
			variants: [
				{ value: 'Tan', label: 'Tan' },
				{ value: 'Gray', label: 'Gray' },
			],
			serialNumber: 'SRI-0202011',
			imagePlaceholder: 'Die & Model',
			imagePath:
				'assets/images/products/SprintRay-LBL-DM2-Gray-0202011.png',
		},
		{
			es: { name: 'Die & Model 2 Tan/Gray 1kg' },
			de: { name: 'Modell 2 Braun/Grau 1kg' },
		}
	),
	withTranslations(
		{
			id: 'resin-nightguard-flex',
			category: 'Resins',
			name: 'NightGuard Flex 1 kg',
			price: 450,
			serialNumber: 'SRI-0202015',
			imagePlaceholder: 'NightGuard Flex',
			imagePath:
				'assets/images/products/SprintRay-LBL-NGFlex-0202015.png',
		},
		{
			es: { name: 'NightGuard Flex 1 kg' },
			de: { name: 'NightGuard Flex 1 kg' },
		}
	),
	withTranslations(
		{
			id: 'resin-retainer',
			category: 'Resins',
			name: 'Retainer 500g',
			price: 479,
			serialNumber: 'SRI-0202161',
			imagePlaceholder: 'Retainer',
			imagePath:
				'assets/images/products/SRI-0202161SprintRayRetainer-500g.png',
		},
		{
			es: { name: 'Retainer 500g' },
			de: { name: 'Retainer 500g' },
		}
	),
	withTranslations(
		{
			id: 'resin-apex-base',
			category: 'Resins',
			name: 'Apex Base 1kg',
			price: 679,
			variants: [
				{ value: 'Light Pink', label: 'Light Pink' },
				{ value: 'Original Pink', label: 'Original Pink' },
				{ value: 'Dark Reddish Pink', label: 'Dark Reddish Pink' },
				{
					value: 'Opaque Original Pink',
					label: 'Opaque Original Pink',
				},
			],
			serialNumber: 'SRI-0202153',
			imagePlaceholder: 'Apex Base',
			imagePath:
				'assets/images/products/APEX-SprintRay-Denture-Base-OpaqueOriginalPink.png',
		},
		{
			es: { name: 'Apex Base 1kg' },
			de: { name: 'Apex Base 1kg' },
		}
	),
	withTranslations(
		{
			id: 'resin-apex-teeth',
			category: 'Resins',
			name: 'Apex Teeth 500g',
			price: 399,
			variants: [
				{ value: 'A1', label: 'A1' },
				{ value: 'A2', label: 'A2' },
				{ value: 'B1', label: 'B1' },
				{ value: 'Bleach', label: 'Bleach' },
			],
			serialNumber: 'SRI-0202157',
			imagePlaceholder: 'Apex Teeth',
			imagePath:
				'assets/images/products/APEX-SprintRay-Denture-Teeth-A1.png',
		},
		{
			es: { name: 'Apex Teeth 500g' },
			de: { name: 'Apex Teeth 500g' },
		}
	),
	withTranslations(
		{
			id: 'resin-surgical-guide',
			category: 'Resins',
			name: 'Surgical Guide 3 1 kg',
			price: 349,
			freeResinValue: 349,
			serialNumber: 'SRI-0205011',
			imagePlaceholder: 'Surgical Guide 3',
			imagePath: 'assets/images/products/SprintRay-LBL-SG3-0205011.png',
		},
		{
			es: { name: 'Surgical Guide 3 1 kg' },
			de: { name: 'Bohrschablone 3 1 kg' },
		}
	),
	withTranslations(
		{
			id: 'resin-nightguard-firm',
			category: 'Resins',
			name: 'Nightguard Firm 2 1 kg',
			price: 450,
			serialNumber: 'SRI-0202147',
			imagePlaceholder: 'Nightguard Firm 2',
			imagePath:
				'assets/images/products/SprintRay-NGFirm2-MidnightPurple-1kg.png',
		},
		{
			es: { name: 'Nightguard Firm 2 1 kg' },
			de: { name: 'Nightguard Firm 2 1 kg' },
		}
	),
	withTranslations(
		{
			id: 'resin-dental-model',
			category: 'Resins',
			name: 'Dental Model 3 kg',
			price: 319,
			freeResinValue: 319,
			variants: DUNE_GRAPHITE,
			serialNumber: 'SRI-0620001',
			imagePlaceholder: 'Dental Model',
			imagePath:
				'assets/images/products/SprintRay-DentalModel-Dune-1Gallon-EU.png',
		},
		{
			es: { name: 'Dental Model 3 kg' },
			de: { name: 'Dentalmodell 3 kg' },
		}
	),
	// Cápsulas Midas
	withTranslations(
		{
			id: 'midas-ceramic-crown',
			category: 'Cápsulas Midas',
			name: 'Ceramic Crown 3-pack',
			price: 59,
			variants: A1_A2_A3_B1_BLEACH,
			serialNumber: 'SRI-0204010',
			imagePlaceholder: 'Ceramic Crown 3-pack',
			imagePath:
				'assets/images/products/sprintray-Midas-Capsule-packaging-generic.png',
		},
		{
			es: { name: 'Ceramic Crown 3-pack' },
			de: { name: 'Keramikkrone 3-Pack' },
		}
	),
	withTranslations(
		{
			id: 'midas-crown-ht',
			category: 'Cápsulas Midas',
			name: 'Crown HT 3-pack',
			price: 65,
			variants: [
				...A1_A2_A3_B1_BLEACH,
				{ value: 'unpigmented', label: 'Unpigmented' },
			],
			serialNumber: 'SRI-0204030',
			imagePlaceholder: 'Crown HT 3-pack',
			imagePath:
				'assets/images/products/SprintRay-CrownHT-A1-Midas3pack.png',
		},
		{
			es: { name: 'Crown HT 3-pack' },
			de: { name: 'Crown HT 3-Pack' },
		}
	),
	withTranslations(
		{
			id: 'midas-onx-tough',
			category: 'Cápsulas Midas',
			name: 'OnX Tough 2 3-pack',
			price: 69,
			variants: A1_BLEACH,
			serialNumber: 'SRI-0204053',
			imagePlaceholder: 'OnX Tough 2 3-pack',
			imagePath:
				'assets/images/products/SRI-0104053-SprintRay-OnXTough2-A1-Midas3pack.png',
		},
		{
			es: { name: 'OnX Tough 2 3-pack' },
			de: { name: 'OnX Tough 2 3-Pack' },
		}
	),
	// Accessories
	withTranslations(
		{
			id: 'acc-plataforma-std',
			category: 'Accessories',
			name: 'Standard Build Platform',
			price: 400,
			serialNumber: 'SRI-0503035',
			imagePlaceholder: 'Standard Build Platform',
			imagePath:
				'assets/images/products/SRI-0503035-SprintRay-Pro-2-Standard-Build-Platform.png',
		},
		{
			es: { name: 'Plataforma Standard' },
			de: { name: 'Tablett Standard' },
			it: { name: 'Piattello Standard' },
		}
	),
	withTranslations(
		{
			id: 'acc-tanque-std',
			category: 'Accessories',
			name: 'Standard Resin Tank',
			price: 350,
			serialNumber: 'SRI-0503034',
			imagePlaceholder: 'Standard Resin Tank',
			imagePath:
				'assets/images/products/SRI-0503034-SprintRay-Pro-2-Standard-Resin-Tank.png',
		},
		{
			es: { name: 'Tanque Standard' },
			de: { name: 'Standard-Tank' },
			it: { name: 'Tank Standard' },
		}
	),
	withTranslations(
		{
			id: 'acc-plataforma',
			category: 'Accessories',
			name: 'Arch Kit Platform',
			price: 990,
			serialNumber: 'SRI-0503046',
			imagePlaceholder: 'Arch Kit Platform',
			imagePath: 'assets/images/products/Pro2_ArchkitPlatform.png',
		},
		{
			es: { name: 'Plataforma Arch Kit' },
			de: { name: 'Tablett Arch Kit' },
			it: { name: 'Piattello Arch Kit' },
		}
	),
	withTranslations(
		{
			id: 'acc-kit-tanque',
			category: 'Accessories',
			name: 'Arch Kit Tank',
			price: 490,
			serialNumber: 'SRI-0503047',
			imagePlaceholder: 'Arch Kit Tank',
			imagePath: 'assets/images/products/tank-archkit.png',
		},
		{
			es: { name: 'Tanque Arch Kit' },
			de: { name: 'Tank Arch Kit' },
			it: { name: 'Tank Arch Kit' },
		}
	),
	withTranslations(
		{
			id: 'acc-duo-kit',
			category: 'Accessories',
			name: 'Duo Kit (Adapter, Platform, 2x Tank)',
			price: 2350,
			serialNumber: 'SRI-0503072',
			imagePlaceholder: 'Duo Kit',
			imagePath:
				'assets/images/products/DuoKIt_Assembled_Transparent_2800px-1.png',
		},
		{
			es: { name: 'Duo Kit (Adaptador, Plataforma, 2x Tanque)' },
			de: { name: 'Duo Kit (Adapter, Tablett, 2x Tank)' },
			it: { name: 'Duo Kit (Adattatore, Piattello, 2x Tank)' },
		}
	),
];

// Helper to add translations to a promotion
const withPromoTranslations = (
	promo: Promotion,
	translations: {
		es?: { title?: string; description?: string };
		de?: { title?: string; description?: string };
		it?: { title?: string; description?: string };
	}
): Promotion => ( { ...promo, translations } );

// Promotions
export const PROMOTIONS: Promotion[] = [
	withPromoTranslations(
		{
			id: 'pro2-system',
			title: 'Pro 2 Complete System + Free Arch Kit',
			description:
				'Pro 2, ProWash S, NanoCure, Arch Kit Tank and platform',
			originalPrice: 18450,
			promoPrice: 16970,
			includes: [ 'Pro 2', 'ProWash S', 'NanoCure', 'Arch Kit' ],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Sistema-completo-Pro2-550x300.png',
		},
		{
			es: {
				title: 'Sistema completo Pro 2 + Arch Kit gratis',
				description:
					'Pro 2, ProWash S, NanoCure, Arch Kit Tanque y plataforma',
			},
			de: {
				title: 'Pro 2 Komplettsystem + Arch Kit kostenlos',
				description:
					'Pro 2, ProWash S, NanoCure, Arch Kit Tank und Plattform',
			},
			it: {
				title: 'Sistema Pro 2 + Arch Kit GRATIS',
				description:
					'Pro 2, ProWash S, NanoCure, Tank e piattello Arch Kit',
			},
		}
	),
	withPromoTranslations(
		{
			id: 'midas-offer',
			title: 'SprintRay Midas Offer',
			description:
				'Midas, NanoCure, 6x 3-pack Ceramic Crown, 2 years ProTection Plan',
			originalPrice: 16384,
			promoPrice: 15154,
			includes: [
				'Midas',
				'NanoCure',
				'6x Ceramic Crown 3-pack',
				'ProTection Plan',
			],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Oferta-SprintRay-Midas-550x300.png',
		},
		{
			es: {
				title: 'Oferta SprintRay Midas',
				description:
					'Midas, NanoCure, 6x 3-pack Ceramic Crown, 2 años ProTection Plan',
			},
			de: {
				title: 'SprintRay Midas Angebot',
				description:
					'Midas, NanoCure, 6x 3-Pack Keramikkrone, 2 Jahre ProTection Plan',
			},
			it: {
				title: 'Midas + 3x confezioni Crown HT GRATIS',
				description:
					'Midas, NanoCure, 6x 3-pack Ceramic Crown, 2 anni Piano ProTection',
			},
		}
	),
	withPromoTranslations(
		{
			id: 'complete-bundle',
			title: 'SprintRay Pro 2 and Midas Complete Bundle',
			description:
				'+ FREE Arch Kit. Midas, Pro 2, ProWash S, NanoCure, 3x 3-pack Ceramic Crown, 2 years ProTection Plan',
			originalPrice: 33952,
			promoPrice: 26990,
			includes: [
				'Midas',
				'Pro 2',
				'ProWash S',
				'NanoCure',
				'Arch Kit GRATIS',
				'3x Ceramic Crown 3-pack',
				'ProTection Plan',
			],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Pack-completo-Pro2-Midas-550x300.png',
		},
		{
			es: {
				title: 'Pack completo SprintRay Pro 2 y Midas',
				description:
					'+ Arch Kit GRATIS. Midas, Pro 2, ProWash S, NanoCure, 3x 3-pack Ceramic Crown, 2 años ProTection Plan',
			},
			de: {
				title: 'SprintRay Pro 2 und Midas Komplettpaket',
				description:
					'+ KOSTENLOSER Arch Kit. Midas, Pro 2, ProWash S, NanoCure, 3x 3-Pack Keramikkrone, 2 Jahre ProTection Plan',
			},
			it: {
				title: 'Bundle Midas + Pro 2',
				description:
					'+ Arch Kit GRATIS. Midas, Pro 2, ProWash S, NanoCure, 3x 3-pack Ceramic Crown, 2 anni Piano ProTection',
			},
		}
	),
];

// Helper to add translations to a free resin option
const withResinTranslations = (
	resin: FreeResinOption,
	translations: {
		es?: { name?: string };
		de?: { name?: string };
		it?: { name?: string };
	}
): FreeResinOption => ( { ...resin, translations } );

// Free Resin Options - ordered by price (most expensive to least expensive)
export const FREE_RESIN_OPTIONS: FreeResinOption[] = [
	withResinTranslations(
		{ id: 'free-onx-tough', name: 'OnX Tough 2', value: 749 },
		{
			es: { name: 'OnX Tough 2' },
			de: { name: 'OnX Tough 2' },
		}
	),
	withResinTranslations(
		{ id: 'free-apex-base', name: 'Apex Base', value: 679 },
		{
			es: { name: 'Apex Base' },
			de: { name: 'Apex Base' },
		}
	),
	withResinTranslations(
		{ id: 'free-retainer', name: 'Retainer', value: 479 },
		{
			es: { name: 'Retainer' },
			de: { name: 'Retainer' },
		}
	),
	withResinTranslations(
		{ id: 'free-nightguard-flex', name: 'NightGuard Flex', value: 450 },
		{
			es: { name: 'NightGuard Flex' },
			de: { name: 'NightGuard Flex' },
		}
	),
	withResinTranslations(
		{ id: 'free-nightguard-firm', name: 'Nightguard Firm 2', value: 450 },
		{
			es: { name: 'Nightguard Firm 2' },
			de: { name: 'Nightguard Firm 2' },
		}
	),
	withResinTranslations(
		{ id: 'free-apex-teeth', name: 'Apex Teeth', value: 399 },
		{
			es: { name: 'Apex Teeth' },
			de: { name: 'Apex Teeth' },
		}
	),
	withResinTranslations(
		{ id: 'free-ceramic-crown', name: 'Ceramic Crown', value: 390 },
		{
			es: { name: 'Ceramic Crown' },
			de: { name: 'Keramikkrone' },
		}
	),
	withResinTranslations(
		{ id: 'free-dental-model', name: 'Dental Model 3 kg', value: 319 },
		{
			es: { name: 'Dental Model 3 kg' },
			de: { name: 'Dentalmodell 3 kg' },
			it: { name: 'Modelllo Dentale 3 kg' },
		}
	),
	withResinTranslations(
		{ id: 'free-surgical-guide', name: 'Surgical Guide 3', value: 349 },
		{
			es: { name: 'Surgical Guide 3' },
			de: { name: 'Bohrschablone 3' },
			it: { name: 'Guida Chirurgica 3' },
		}
	),
	withResinTranslations(
		{ id: 'free-tanque-pro2', name: 'Pro 2 Resin Tank', value: 350 },
		{
			es: { name: 'Tanque Pro 2' },
			de: { name: 'Pro 2 Harztank' },
			it: { name: 'Tank Pro 2' },
		}
	),
	withResinTranslations(
		{ id: 'free-die-model', name: 'Die & Model', value: 149 },
		{
			es: { name: 'Die & Model' },
			de: { name: 'Modell' },
		}
	),
];

const MAX_FREE_RESINS_BUDGET = 1000;

// German UI Text
export const GERMAN_TEXT: UIText = {
	header: {
		title: 'EXPODENTAL',
		website: 'www.sprintray.com/de-de',
		email: 'orders.eu@sprintray.com',
	},
	customerInfo: {
		title: 'Kundeninformation',
		nombreClinica: 'Klinik/Labor Name',
		nombre: 'Vorname',
		apellidos: 'Nachname',
		direccion: 'Adresse',
		codigoPostal: 'Postleitzahl',
		ciudad: 'Stadt',
		pais: 'Land',
		email: 'E-Mail',
		telefono: 'Telefon',
		required: '*',
		requestTypeLabel: 'Art der Anfrage',
		requestTypeOrder: 'Bestellung',
		requestTypeQuote: 'Angebotsanfrage',
	},
	tradePartner: {
		title: 'Handelspartner',
		distribuidor: 'Händler',
		nombreFachberater: 'Name Fachberater',
		selectDistributor: 'Händler auswählen',
		selectCountryFirst: 'Bitte zuerst ein Land auswählen',
		distribuidorManualPlaceholder: 'Handelspartner eingeben',
		manualEntryHint: 'Kein gelisteter Händler für dieses Land — bitte den Namen des Handelspartners manuell eingeben.',
	},
	products: {
		title: 'Produkte',
		addToCart: 'Hinzufügen',
		quantity: 'Menge',
		selectVariant: 'Variante auswählen...',
		categories: {
			Hardware: 'Hardware',
			Resins: 'Harze',
			'Cápsulas Midas': 'Midas Kapseln',
			Accessories: 'Zubehör',
		},
	},
	promotions: {
		title: 'ANGEBOTE',
		validUntil: 'Gültig bis 20.03.2026',
		add: 'Hinzufügen',
		added: 'Hinzugefügt',
	},
	freeResins: {
		title: '1.000€ GRATIS Harze',
		subtitle: 'Wählen Sie Ihre kostenlosen Harze (maximal 1.000€)',
		budget: 'Verwendetes Budget',
		select: 'Auswählen',
		disabledMessage:
			'Fügen Sie ein Pro 2 System oder ein + Pack hinzu, um die kostenlosen Harze zu aktivieren',
	},
	cart: {
		empty: 'Keine Produkte im Warenkorb',
		title: 'Bestellübersicht',
	},
	footer: {
		remarks: 'Bemerkungen',
		disclaimer:
			'Alle Preise sind in Euro und ohne MwSt.\n\nNach dem Kauf des Systems über Ihren Vertriebspartner senden Sie eine E-Mail an orders.eu@sprintray.com mit Zahlungsbeleg oder Rechnung, damit wir Ihnen die kostenlosen Harze zusenden können.',
		signature: 'Datum und Unterschrift des Kunden',
		clearSignature: 'Löschen',
		submitOrder: 'Bestellung senden',
		submitting: 'Wird gesendet...',
		success: 'Bestellung erfolgreich gesendet!',
		error: 'Fehler beim Senden der Bestellung. Bitte versuchen Sie es erneut.',
		offline: 'Offline-Modus',
		offlineMessage:
			'Ihre Bestellung wird lokal gespeichert und gesendet, wenn die Verbindung wiederhergestellt ist.',
		online: 'Online',
		syncing: 'Synchronisiere',
		pendingOrders: 'ausstehende Bestellungen',
		pendingOrder: 'ausstehende Bestellung',
	},
	print: {
		title: 'Angebotsanfrage',
		eventName: 'Veranstaltungsname',
	},
};

export { MAX_FREE_RESINS_BUDGET };

// ============================================================================
// FREE PACKAGE OPTIONS (SprintRace Advantage)
// ============================================================================

// Helper to add translations to a package option
const withPackageTranslations = (
	pkg: any,
	translations: {
		es?: { name?: string; description?: string };
		de?: { name?: string; description?: string };
		it?: { name?: string; description?: string };
	}
): any => ( { ...pkg, translations } );

// Free Package Options for SprintRace Advantage promotions
export const FREE_PACKAGE_OPTIONS = [
	// Resin Packages (Point 1)
	withPackageTranslations(
		{
			id: 'pkg-onx-tough',
			name: '1x OnX Tough 2 Resin',
			description: 'Color selection as per Resin Add',
			value: 749,
			type: 'resin',
			products: [ { productId: 'resin-onx-tough', quantity: 1 } ],
			variants: [
				...A1_BLEACH,
				{ value: 'Hollywood White', label: 'Hollywood White' },
			],
			hasVariants: true,
		},
		{
			es: { name: '1x Resina OnX Tough 2' },
			de: { name: '1x OnX Tough 2 Harz' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-retainer-2x',
			name: '2x Retainer Resin',
			description: 'Two 500g bottles',
			value: 958,
			type: 'resin',
			products: [ { productId: 'resin-retainer', quantity: 2 } ],
		},
		{
			es: { name: '2x Resina Retainer' },
			de: { name: '2x Retainer Harz' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-nightguard-firm',
			name: '2x NightGuard Firm Resin',
			description: 'Two 1 kg bottles',
			value: 900,
			type: 'resin',
			products: [ { productId: 'resin-nightguard-firm', quantity: 2 } ],
		},
		{
			es: { name: '2x Resina NightGuard Firm' },
			de: { name: '2x NightGuard Firm Harz' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-nightguard-flex',
			name: '2x NightGuard Flex',
			description: 'Two 1 kg bottles',
			value: 900,
			type: 'resin',
			products: [ { productId: 'resin-nightguard-flex', quantity: 2 } ],
		},
		{
			es: { name: '2x NightGuard Flex' },
			de: { name: '2x NightGuard Flex' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-apex-base-teeth',
			name: '1x Apex Base & 1x Apex Teeth',
			description: 'Denture base and teeth set',
			value: 1078,
			type: 'resin',
			products: [
				{ productId: 'resin-apex-base', quantity: 1 },
				{ productId: 'resin-apex-teeth', quantity: 1 },
			],
			hasVariants: true,
		},
		{
			es: { name: '1x Apex Base y 1x Apex Teeth' },
			de: { name: '1x Apex Base & 1x Apex Teeth' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-model-resin-3x',
			name: '3x 3-Liter Model Resin',
			description: 'Clear, Grey, or Tan color selection',
			value: 957,
			type: 'resin',
			products: [ { productId: 'resin-dental-model', quantity: 3 } ],
			variants: DUNE_GRAPHITE,
			hasVariants: true,
		},
		{
			es: { name: '3x Resina Modelo 3 Litros' },
			de: { name: '3x 3-Liter Modellharz' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-die-model-6x',
			name: '6x 1 Liter Die & Model Tan',
			description: 'Six 1kg bottles',
			value: 894,
			type: 'resin',
			products: [ { productId: 'resin-die-model', quantity: 6 } ],
			variants: [
				{ value: 'Tan', label: 'Tan' },
				{ value: 'Gray', label: 'Gray' },
			],
			hasVariants: true,
		},
		{
			es: { name: '6x 1 Litro Die & Model Tan' },
			de: { name: '6x 1 Liter Die & Model Tan' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-pro2-tanks-2x',
			name: '2x Pro 2 Tanks',
			description: 'Standard resin tanks for Pro 2',
			value: 700,
			type: 'resin',
			products: [ { productId: 'acc-tanque-std', quantity: 2 } ],
		},
		{
			es: { name: '2x Tanques Pro 2' },
			de: { name: '2x Pro 2 Harztanks' },
			it: { name: '2x Tank Pro 2' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-surgical-guide-1x',
			name: '1x Surgical Guide 3 1 kg',
			description: 'Surgical Guide 3 resin 1 kg',
			value: 349,
			type: 'resin',
			products: [ { productId: 'resin-surgical-guide', quantity: 1 } ],
		},
		{
			es: { name: '1x Guía Quirúrgica 3 1 kg' },
			de: { name: '1x Bohrschablone 3 1 kg' },
			it: { name: '1x Guida Chirurgica 3 1 kg' },
		}
	),
	withPackageTranslations(
		{
			id: 'pkg-dental-model-1x',
			name: '1x Dental Model 3 kg',
			description: 'Dental Model resin 3 kg with color selection',
			value: 319,
			type: 'resin',
			products: [ { productId: 'resin-dental-model', quantity: 1 } ],
			variants: DUNE_GRAPHITE,
			hasVariants: true,
		},
		{
			es: { name: '1x Resina Modelo Dental 3 kg' },
			de: { name: '1x Dentalmodell 3 kg' },
			it: { name: '1x Modelllo Dentale 3 kg' },
		}
	),
	// Add-on Packages (Point 2)
	withPackageTranslations(
		{
			id: 'addon-crown-ht',
			name: '8x Crown HT Clamshells',
			description: '3er Packs / 520 €',
			value: 520,
			type: 'addon',
			products: [ { productId: 'midas-crown-ht', quantity: 3 } ],
			variants: [
				...A1_A2_A3_B1_BLEACH,
				{ value: 'unpigmented', label: 'Unpigmented' },
			],
			hasVariants: true,
		},
		{
			es: { name: '8x Crown HT Clamshells (3 Packs / 520 €)' },
			de: { name: '8x Crown HT Clamshells (3er Packs / 520 €)' },
		}
	),
	withPackageTranslations(
		{
			id: 'addon-ceramic-crown',
			name: '8x Ceramic Crown Clamshells',
			description: '3er Packs / 472 €',
			value: 472,
			type: 'addon',
			products: [ { productId: 'midas-ceramic-crown', quantity: 3 } ],
			variants: A1_A2_A3_B1_BLEACH,
			hasVariants: true,
		},
		{
			es: { name: '8x Ceramic Crown Clamshells (3 Packs / 472 €)' },
			de: { name: '8x Keramikkrone Clamshells (3er Packs / 472 €)' },
		}
	),
	withPackageTranslations(
		{
			id: 'addon-crown-ht-ceramic-mix',
			name: '4x Crown HT Clamshells, 4x Ceramic Crown Clamshells',
			description: 'Mixed Crown Pack / 496 €',
			value: 496,
			type: 'addon',
			products: [
				{ productId: 'midas-crown-ht', quantity: 1.5 },
				{ productId: 'midas-ceramic-crown', quantity: 1.5 },
			],
			variants: A1_A2_A3_B1_BLEACH,
			hasVariants: true,
		},
		{
			es: { name: '4x Crown HT, 4x Ceramic Crown (Pack Mixto / 496 €)' },
			de: { name: '4x Crown HT, 4x Keramikkrone (Mix-Pack / 496 €)' },
			it: { name: '4x Crown HT, 4x Ceramic Crown (Pacchetto Misto / 496 €)' },
		}
	),
];

// New SprintRace Advantage Promotions
export const NEW_PROMOTIONS = [
	withPromoTranslations(
		{
			id: 'upgrade-offer-midas' as PromotionType,
			title: 'Upgrade Offer: Pro 2 for Midas Customers',
			description: 'Pro 2, ProWash S, ProTection Plan for both devices',
			originalPrice: 16375,
			promoPrice: 12490,
			includes: [ 'Pro 2', 'ProWash S', 'ProTection Plan' ],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Pro_2_3-quarter-closed-right-Punchier.png',
		},
		{
			es: {
				title: 'Oferta de actualización: Pro 2 para clientes Midas',
				description:
					'Pro 2, ProWash S, Plan ProTection para ambos dispositivos',
			},
			de: {
				title: 'Upgrade-Angebot: Pro 2 für Midas-Kunden',
				description:
					'Pro 2, ProWash S, ProTection Plan für beide Geräte',
			},
			it: {
				title: 'Aggiunta Pro 2 a Sistema Midas',
				description:
					'Pro 2, ProWash S, Piano ProTection per entrambi i dispositivi',
			},
		}
	),
	withPromoTranslations(
		{
			id: 'update-offer-midas-pro' as PromotionType,
			title: 'Update Offer: Midas for Pro (2/95/55/S) Customers',
			description: 'Midas, NanoCure, ProTection Plan for both devices',
			originalPrice: 12735,
			promoPrice: 9940,
			includes: [ 'Midas', 'NanoCure', 'ProTection Plan' ],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [ 'addon-crown-ht', 'addon-ceramic-crown' ],
			imagePath: 'assets/images/products/Midas-Hero-front.png',
		},
		{
			es: {
				title: 'Oferta de actualización: Midas para clientes Pro (2/95/55/S)',
				description:
					'Midas, NanoCure, Plan ProTection para ambos dispositivos',
			},
			de: {
				title: 'Update-Angebot: Midas für Pro (2/95/55/S) Kunden',
				description:
					'Midas, NanoCure, ProTection Plan für beide Geräte',
			},
			it: {
				title: 'Aggiunta Midas a Sistema Pro 2',
				description:
					'Midas, NanoCure, Piano ProTection per entrambi i dispositivi',
			},
		}
	),
	withPromoTranslations(
		{
			id: 'tradeup-pro2' as PromotionType,
			title: 'Trade Up: Pro 2 for existing Pro 95/55 S printers',
			description: 'Pro 2 with package selection',
			originalPrice: 12540,
			promoPrice: 8990,
			includes: [ 'Pro 2' ],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Pro_2_3-quarter-closed-right-Punchier.png',
		},
		{
			es: {
				title: 'Trade Up: Pro 2 para impresoras Pro 95/55 S existentes',
				description: 'Pro 2 con selección de paquete',
			},
			de: {
				title: 'Trade Up: Pro 2 für vorhandene Pro 95/55 S Drucker',
				description: 'Pro 2 mit Paketauswahl',
			},
			it: {
				title: 'Trade Up: Stampante Pro (S)',
				description: 'Pro 2 con selezione del pacchetto',
			},
		}
	),
	withPromoTranslations(
		{
			id: 'tradeup-complete-system' as PromotionType,
			title: 'Trade Up: Complete System',
			description: 'Pro 2 + Midas with package selection',
			originalPrice: 19755,
			promoPrice: 13990,
			includes: [ 'Pro 2', 'Midas' ],
			includesFreeResins: false,
			packageMode: 'packages',
			availablePackages: [
				'pkg-onx-tough',
				'pkg-retainer-2x',
				'pkg-nightguard-firm',
				'pkg-nightguard-flex',
				'pkg-apex-base-teeth',
				'pkg-model-resin-3x',
				'pkg-die-model-6x',
				'pkg-pro2-tanks-2x',
				'addon-crown-ht',
				'addon-ceramic-crown',
			],
			imagePath:
				'assets/images/products/Pack-completo-Pro2-Midas-550x300.png',
		},
		{
			es: {
				title: 'Trade Up: Sistema Completo',
				description: 'Pro 2 + Midas con selección de paquete',
			},
			de: {
				title: 'Trade-up-Angebot: Komplettsystem',
				description: 'Pro 2 + Midas mit Paketauswahl',
			},
			it: {
				title: 'Trade Up: Sistema completo Pro (S)',
				description: 'Pro 2 + Midas con selezione del pacchetto',
			},
		}
	),
];

// Merge new promotions with existing ones
export const ALL_PROMOTIONS = [ ...PROMOTIONS, ...NEW_PROMOTIONS ];
